import { useQuery } from '@apollo/client';
import { Box, DialogActions, styled } from '@mui/material';
import { BlockContainer } from 'components/ChainMapping/Forms/styles';
import { Loader } from 'components/Forms';
import { useDialog } from 'components/hooks';
import { DialogInner } from 'components/Partners/Partners.styles';
import { ErrorState } from 'components/Structure';
import { DialogDefault, ThemeButton } from 'designSystem';
import { GET_CLUSTER_SITE_IDS } from 'graphql/secure/queries/site.queries';
import useSites from 'hooks/useSites';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBasePartnerCompany } from 'types/partner.types';
import { ESiteType, IClusterSite, IFarmSite, SiteViewOption } from 'types/site.types';
import { GraphQlConnection } from 'types/types';
import { removeGraphConnections } from 'utils/graphql.utils';
import useSiteMutations from '../hooks/useSiteMutations';
import ManageSiteSummary, { TManageSiteErrors } from './ManageSiteSummary';
import SiteSelection from './SiteSelection';

interface IManageClusterSitesDialogProps extends IDefaultDialogProps {
  siteCluster: IClusterSite;
  partner?: IBasePartnerCompany;
}

const StyledBlockContainer = styled(BlockContainer)({
  paddingTop: 0,
  height: '100%',
});

const ManageClusterSitesDialog: FC<IManageClusterSitesDialogProps> = ({
  onClose,
  open,
  siteCluster,
}) => {
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);

  const { updateSite, loading: loadingMutation } = useSiteMutations();
  const { closeDialog: onCloseAll } = useDialog();

  const {
    sites,
    loading,
    hasNextPage,
    searchTerm,
    filters,
    handlePageEndReached,
    handleSearchTermChange,
    handleFilterChange,
    handleFilterReset,
  } = useSites({
    skipCache: true,
    skipUseQueryParams: true,
    defaultViewOption: SiteViewOption.LIST,
    defaultSiteTypes: [
      ESiteType.FARM,
      ESiteType.COLLECTION_SITE,
      ESiteType.FOREST,
      ESiteType.GENERIC_SITE,
      ESiteType.MINE,
      ESiteType.ORIGIN_SITE,
    ],
  });

  const {
    data: clusterSiteIdsData,
    loading: clusterSiteIdsDataLoading,
    error,
  } = useQuery<{ site: { sites: GraphQlConnection<{ id: string }> } }>(GET_CLUSTER_SITE_IDS, {
    variables: { id: siteCluster.id },
  });

  const firstSelectedSite = useMemo(() => {
    if (siteCluster.sitesType === ESiteType.FARM && siteCluster.sites.edges.length) {
      return siteCluster.sites.edges[0].node as IFarmSite;
    } else if (selectedSiteIds.length) {
      const firstSite = sites.find(site => site.id === selectedSiteIds[0]);
      return firstSite?.siteType === ESiteType.FARM ? (firstSite as IFarmSite) : undefined;
    }
  }, [siteCluster, sites, selectedSiteIds]);

  const errors = useMemo(() => {
    // Enable button if no sites are selected
    if (selectedSiteIds.length === 0) return {};

    return selectedSiteIds?.reduce((prevErrors, id) => {
      const site = sites.find(s => s.id === id);
      if (!site) {
        return prevErrors; // Skip validation if site is not found because of pagination
      }

      // Check if all sites have the same siteType
      if (
        (siteCluster.sitesType && siteCluster.sitesType !== site.siteType) ||
        firstSelectedSite?.siteType !== site.siteType
      ) {
        prevErrors = { ...prevErrors, siteType: 'Site type: Invalid site type' };
      }

      // Check if all sites have the same raw material
      if (
        (siteCluster.sitesType === ESiteType.FARM &&
          site.siteType === ESiteType.FARM &&
          site.rawMaterial.id !== firstSelectedSite?.rawMaterial.id) ||
        (firstSelectedSite?.siteType === ESiteType.FARM &&
          site.siteType === ESiteType.FARM &&
          firstSelectedSite.rawMaterial.id !== site.rawMaterial.id)
      ) {
        prevErrors = { ...prevErrors, rawMaterial: 'Invalid raw material' };
      }

      // Check if all sites are in the same country
      if (
        (siteCluster.countryCode && siteCluster.countryCode !== site.countryCode) ||
        (firstSelectedSite && firstSelectedSite.countryCode !== site?.countryCode)
      ) {
        prevErrors = { ...prevErrors, country: 'Invalid country' };
      }

      return prevErrors;
    }, {} as TManageSiteErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteIds, siteCluster, sites, firstSelectedSite]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleAddSites = async () => {
    await updateSite({
      variables: {
        id: siteCluster.id,
        input: {
          siteType: ESiteType.CLUSTER,
          siteClusterInput: {
            siteIds: selectedSiteIds,
          },
        },
      },
    });
    onCloseAll?.();
  };

  useEffect(() => {
    // select sites that are already in the cluster
    if (clusterSiteIdsData?.site.sites.edges?.length) {
      const siteClusterIds = removeGraphConnections(clusterSiteIdsData.site.sites).map(
        ({ id }) => id
      );
      setSelectedSiteIds(siteClusterIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterSiteIdsData]);

  return (
    <DialogDefault
      title="Manage sites in cluster"
      onClose={onClose}
      open={open}
      fullWidth
      iconName="site-cluster-manage"
      maxWidth="xl"
      background="#fff"
    >
      <DialogInner container minHeight={620}>
        {clusterSiteIdsDataLoading ? (
          <Loader />
        ) : error || !clusterSiteIdsData ? (
          <ErrorState />
        ) : (
          <>
            <StyledBlockContainer container z-index={0}>
              <ManageSiteSummary
                site={siteCluster}
                requiredSiteType={firstSelectedSite?.siteType || siteCluster.sitesType}
                requiredRawMaterialTitle={firstSelectedSite?.rawMaterial.title}
                requiredLocationName={firstSelectedSite?.locationName || siteCluster.locationName}
                errors={errors}
              />

              <SiteSelection
                sites={sites}
                selectedSiteIds={selectedSiteIds}
                filters={filters}
                hasNextPage={hasNextPage}
                loading={loading}
                searchTerm={searchTerm}
                placeholderSearchInput="Search sites"
                onSearchTermChange={handleSearchTermChange}
                onSelectionChange={setSelectedSiteIds}
                onPageEndReached={handlePageEndReached}
                onFilterChange={handleFilterChange}
                onFilterReset={handleFilterReset}
              />
            </StyledBlockContainer>
          </>
        )}
      </DialogInner>

      <DialogActions sx={{ width: '100%' }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={onClose}>
            Cancel
          </ThemeButton>
          <ThemeButton
            color="YELLOW"
            size="large"
            type="submit"
            disabled={hasErrors}
            tooltip={hasErrors ? 'Please resolve the errors before saving' : undefined}
            loading={loadingMutation}
            onClick={handleAddSites}
          >
            Save selection {(selectedSiteIds.length && `(${selectedSiteIds.length})`) || ''}
          </ThemeButton>
        </Box>
      </DialogActions>
    </DialogDefault>
  );
};

export default ManageClusterSitesDialog;
